import { IconCopyLink } from '@/assets/svg/icon-copy-link';
import { IconEmail } from '@/assets/svg/icon-email';
import { IconFacebook } from '@/assets/svg/icon-facebook';
import IconSharing from '@/assets/svg/icon-sharing';
import { IconTwitter } from '@/assets/svg/icon-twitter';
import { IconWhatsApp } from '@/assets/svg/icon-whatsapp';
import { styled, css } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { useMediaQuery } from '@/utils/use-media-query';
import { t, Trans } from '@lingui/macro';
import * as clipboard from 'clipboard-polyfill';
import { ComponentType, SVGProps, useEffect, useState } from 'react';
import { Button } from '../elements/button';
import { LinkTo } from '../link-to';

export type SharingProps = {
  title: string;
  text: string;
  url: string;
};

const Root = styled('div', {
  display: 'grid',
  gridTemplateRows: 'repeat(2, auto)',
  justifyContent: 'start',
  color: 'var(--color-text-light)',
});

const Text = styled('p', {
  textAlign: 'center',
});

const Icon = styled('span', {
  display: 'flex',
  alignItems: 'center',
});

export function ShareButton(props: ComposableStyledComponentProps<HTMLDivElement> & SharingProps) {
  const [hasNativeSharing, setHasNativeSharing] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { navigator } = window;
      if (navigator) setHasNativeSharing(!!navigator?.share);
    }
  }, []);

  const isTouchDevice = useMediaQuery('(pointer: coarse)');

  return (
    <Root css={props.css}>
      <Text>
        <Trans id="sharing.title">Share this story</Trans>
      </Text>

      {hasNativeSharing && isTouchDevice ? (
        <NativeShareButton {...props} />
      ) : (
        <FallbackSharingOptions {...props} />
      )}
    </Root>
  );
}

function NativeShareButton(props: SharingProps) {
  const { title, text, url } = props;

  const handleClick = async () => {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
    } catch (e) {
      // don't do anything
    }
  };

  return (
    <Button
      css={{
        color: 'var(--color-text-light)',
        border: '2px solid currentColor',
        borderRadius: '$3',
        paddingBlock: '$2',
        paddingInline: '$3',
        whiteSpace: 'nowrap',

        '&:hover': {
          backgroundColor: '$blue',
        },

        '&.focus-visible, &:focus-visible': {
          backgroundColor: '$blue0',
          borderStyle: 'dotted',
          borderWidth: 3,
          outline: 'none',
        },
      }}
      onClick={handleClick}
    >
      <Icon>
        <IconSharing strokeWidth="2" className={css({ marginInlineEnd: '$2' })()} />
        <Trans id="sharing.button">Share</Trans>
      </Icon>
    </Button>
  );
}

const List = styled('ul', {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gridGap: '$4',
  listStyleType: 'none',
  padding: 0,
  margin: 0,
});

const ListItem = styled('li', {
  whiteSpace: 'nowrap',
});

const CopyToClipBoard = styled('p', {
  fontSize: '$2',
  position: 'absolute',
  insetBlockEnd: '-2.5em',
  insetInlineStart: '50%',
  transform: 'translate(-50%, 0)',
  '[dir=rtl] &': {
    transform: 'translate(50%, 0)',
  },
});

function FallbackSharingOptions(props: SharingProps) {
  const { title, text, url } = props;

  const [isCopiedToClipboard, setCopiedToClipboard] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const handleClick = async () => {
    clipboard.writeText(url);
    setCopiedToClipboard(true);

    const timeoutId = setTimeout(() => {
      setCopiedToClipboard(false);
      setTimeoutId(undefined);
    }, 2000);

    setTimeoutId(timeoutId);
  };

  return (
    <List>
      <ListItem>
        <SharingLinkButton
          Icon={IconWhatsApp}
          label={t({ id: 'sharing.whatsapp.sr-only', message: 'Share this page to WhatsApp' })}
          href={`https://api.whatsapp.com/send?text=${text} Check out ${url}`}
        />
      </ListItem>
      <ListItem>
        <SharingLinkButton
          Icon={IconFacebook}
          label={t({ id: 'sharing.facebook.sr-only', message: 'Share this page on Facebook' })}
          href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
        />
      </ListItem>
      <ListItem>
        <SharingLinkButton
          Icon={IconTwitter}
          label={t({ id: 'sharing.twitter.sr-only', message: 'Share this page on Twitter' })}
          href={`https://twitter.com/intent/tweet?text=${text}&url=${url}`}
        />
      </ListItem>
      <ListItem>
        <SharingLinkButton
          Icon={IconEmail}
          label={t({ id: 'sharing.email.sr-only', message: 'Share this page via email' })}
          href={`mailto:?subject=Check out ${title}&body=Hi ...,%0D%0A%0D%0ACheck out this story ${title} by UNICEF at ${url}.%0D%0A%0D%0ABest, ...`}
        />
      </ListItem>
      <ListItem
        css={{
          position: 'relative',
        }}
      >
        <SharingLinkButton
          Icon={IconCopyLink}
          label={t({ id: 'sharing.copy.sr-only', message: "Copy this page's link to clipboard" })}
          onClick={handleClick}
        />
        {isCopiedToClipboard && (
          <CopyToClipBoard aria-live="polite">
            <Trans id="sharing.copy-link-notification">Link copied!</Trans>
          </CopyToClipBoard>
        )}
      </ListItem>
    </List>
  );
}

const linkStyles = {
  $$size: '3rem',
  padding: 0,
  lineHeight: '$1',
  fontSize: 'inherit',
  position: 'relative',
  display: 'inline-block',
  overflow: 'hidden',
  width: '$$size',
  height: '$$size',
  color: 'var(--color-text-light)',
  borderRadius: '$3',
  border: '1px solid currentColor',

  '&:hover': {
    color: '$blue0',
    borderColor: '$white',
    backgroundColor: '$white',
    backgroundImage: 'none',
  },

  '&:focus-visible, &.focus-visible': {
    outline: '$color$white dotted thick',
    outlineColor: '$white',
    color: '$blue0',
    borderColor: '$white',
    backgroundColor: '$white',
    backgroundImage: 'none',
  },
};

const SharingButtonRoot = styled(Button, linkStyles);

const SharingLinkButtonContent = styled('span', {
  display: 'block',
  position: 'relative',
  width: '100%',
  height: '100%',
});

const iconStyles = css({
  position: 'absolute',
  insetBlockStart: '50%',
  insetInlineStart: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '1.25em',
  '[dir=rtl] &': {
    transform: 'translate(50%, -50%)',
  },
});

type SharingLinkProps = {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

function SharingLinkButton(props: SharingLinkProps) {
  const { Icon, label, href, onClick, ...rest } = props;

  if (href) {
    return (
      <LinkTo href={href} target={'_blank'} rel={'noopener noreferrer'} css={linkStyles} {...rest}>
        <SharingLinkButtonContent>
          <Icon className={iconStyles()} aria-label={label} shapeRendering="crispEdges" />
        </SharingLinkButtonContent>
      </LinkTo>
    );
  }

  return (
    <SharingButtonRoot onClick={onClick} {...rest}>
      <SharingLinkButtonContent>
        <Icon className={iconStyles()} aria-label={label} />
      </SharingLinkButtonContent>
    </SharingButtonRoot>
  );
}
